import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import {
  VIEW_PRESET_PUT_CREATE,
  VIEW_PRESET_DELETE_DELETE_BY_ID,
  VIEW_PRESET_POST_GET_BY_CONTEXT_PROPERTIES,
  VIEW_PRESET_GET_GET_BY_VIEW_ID,
  VIEW_PRESET_POST_UPDATE_BY_ID,
} from '../endpoints/view-preset.endpoints';
import { toHttpParams } from '../api-utils';

import { Observable } from 'rxjs';

// `ViewPreset` and `ViewId` are re-exported from `view-preset.service`, never
// re-declared here: a local copy once drifted six enum values behind, and ~28 ag-grid
// screens import that one.
export {
  ViewPreset,
  ViewId,
} from 'src/app/shared/services/view-preset.service';

import {
  ViewId,
  ViewPreset,
} from 'src/app/shared/services/view-preset.service';

/**
 * Identity of one view preset: the triple
 * POST /view_preset/get_by_context_properties is keyed on.
 */
export interface ViewPresetContext {
  viewId: ViewId;
  contextObjectId: string;
  contextObjectClassId: number;
}

@Injectable({
  providedIn: 'root',
})
export class ViewPresetApiService {
  private readonly http = inject(HttpClient);

  /**
   * The request body is a partial `ViewPreset` - the backend matches on the context
   * triple and ignores the rest. `name: ''` is what every caller sends.
   */
  getViewPresetByContext(context: ViewPresetContext): Observable<ViewPreset> {
    return this.http.post<ViewPreset>(
      VIEW_PRESET_POST_GET_BY_CONTEXT_PROPERTIES,
      {
        view_id: context.viewId,
        context_object_id: context.contextObjectId,
        context_object_class_id: context.contextObjectClassId,
        name: '',
      },
    );
  }

  /**
   * Create (PUT) vs update (POST) branches on `saveAsNew`. Every view-preset caller
   * passes `false`; `true` is the ag-grid "save as new view" path.
   */
  saveViewPreset(
    viewPreset: ViewPreset,
    saveAsNew: boolean = false,
  ): Observable<ViewPreset> {
    return saveAsNew
      ? this.http.put<ViewPreset>(VIEW_PRESET_PUT_CREATE, viewPreset)
      : this.http.post<ViewPreset>(VIEW_PRESET_POST_UPDATE_BY_ID, viewPreset);
  }

  loadAvalableViewPresets(viewId: ViewId): Observable<ViewPreset[]> {
    return this.http.get<ViewPreset[]>(VIEW_PRESET_GET_GET_BY_VIEW_ID, {
      params: toHttpParams({ view_id: viewId }),
    });
  }

  deleteSettings(viewPresetId: string): Observable<unknown> {
    return this.http.delete(VIEW_PRESET_DELETE_DELETE_BY_ID(viewPresetId));
  }
}
